import { Routes, RouterModule } from '@angular/router';

//Layouts
import { SignupLayoutComponent } from "./layouts/signup-layout/signup-layout.component";
import { ErrorLayoutComponent } from "./layouts/error-layout/error-layout.component";
import { PublicLayoutComponent } from './layouts/public-layout/public-layout.component';
import { SecureLayoutComponent } from './layouts/secure-layout/secure-layout.component';

//Components
import { HomeComponent } from './components/home/home.component';
import { PageNotFoundComponent } from '../app/components/error/pagenotfound/pagenotfound.component';

//Professionals
import { SearchComponent } from './components/professionals/pro-search/search.component';
import { ProProfileComponent } from './components/professionals/pro-profile/pro-profile.component';
import { SearchResultsComponent } from './components/professionals/pro-search-results/search-results.component';
import { RedeemSessionComponent } from './components/account/redeemsession/redeemsession.component';

//Auth
import { LoginComponent } from '../app/components/auth/login/login.component';
import { ForgottenComponent } from './components/auth/forgotten/forgotten.component';
import { ResetPasswordComponent } from './components/auth/forgotten/resetpwd.component';
import { UnlockAccountComponent } from './components/auth/unlock/unlockaccount.component';

//Signup
import { Step1Component } from '../app/components/signup/1-signup/step1.component';
import { Step2Component } from '../app/components/signup/2-clientassessment/step2.component';
import { Step3Component } from '../app/components/signup/3-clientprofile/step3.component';
import { Step4Component } from '../app/components/signup/4-accountverification/step4.component';
import { Step5Component } from '../app/components/signup/5-signupcomplete/step5.component';
import { Step2FailComponent } from './components/signup/2-clientassessment/stepfail.component';

//Account
import { ConfirmSessionComponent } from './components/account/confirmsession/confirmsession.component';
import { MessagesComponent } from './components/account/messages/messages.component';
import { MessageViewComponent } from './components/account/messageview/messageview.component';
import { MessageWriteComponent } from './components/account/messagewrite/messagewrite.component';
import { ProfileComponent } from './components/account/profile/profile.component';
import { SessionBookingConfirmedComponent } from './components/account/sessionconfirmed/sessionconfirmed.component';
import { SessionPaymentComponent } from './components/account/sessionpayment/sessionpayment.component';
import { SessionsComponent } from './components/account/sessions/sessions.component';
import { SessionTakenComponent } from './components/account/sessiontaken/sessiontaken.component';
import { SettingsComponent } from './components/account/settings/settings.component';
import { OnDemandComponent } from './components/account/ondemand/ondemand.component';

//Resolve
import { CounsellingTopicResolve } from './resolvers/counsellingtopic.resolve';
import { ReasonForMedicationResolve } from './resolvers/reasonformedication.resolve';
import { CountryResolve } from './resolvers/country.resolve';
import { CallingCodeResolve } from './resolvers/callingcode.resolve';
import { StateResolve } from './resolvers/state.resolve';
import { NationalityResolve } from './resolvers/nationality.resolve';
import { GlobalSettingsResolve } from './resolvers/globalsettings.resolve';
import { WhoAmIResolve } from './resolvers/whoami.resolve';
import { AccountDataResolve } from './resolvers/accountdata.resolve';
import { LanguageResolve } from './resolvers/language.resolve';
import { CounsellorProfileResolve } from './resolvers/counsellorprofile.resolve';

//Guards
import { SignupGuard } from './guards/signup.guard';
import { AuthGuard } from './guards/auth.guard';
import { ProfileImageComponent } from './components/account/profile/profile-image.component';
import { SessionCreditResolve } from './resolvers/session-credit.resolve';
import { OrgIDResolve } from './resolvers/orgid.resolve';

const appRoutes: Routes = [

  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  //Secure Account routing
  {
    path: '',
    component: SecureLayoutComponent,
    children: [
      {
        path: 'dashboard', component: HomeComponent,
        resolve: {
          whoAmI: WhoAmIResolve,
          accountData: AccountDataResolve
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'profile', component: ProfileComponent,
        resolve: {
          countryList: CountryResolve,
          callingCodeList: CallingCodeResolve,
          stateList: StateResolve,
          nationalityList: NationalityResolve,
          globalSettings: GlobalSettingsResolve,
          whoAmI: WhoAmIResolve,
          accountData: AccountDataResolve
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'profile-image', component: ProfileImageComponent,
        resolve: {
          globalSettings: GlobalSettingsResolve,
          whoAmI: WhoAmIResolve,
          accountData: AccountDataResolve
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'messages', component: MessagesComponent,
        resolve: {
          globalSettings: GlobalSettingsResolve,
          whoAmI: WhoAmIResolve,
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'ondemand', component: OnDemandComponent,
        resolve: {
          whoAmI: WhoAmIResolve,
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'messageview/:id', component: MessageViewComponent,
        resolve: {
          globalSettings: GlobalSettingsResolve,
          whoAmI: WhoAmIResolve,
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'messagewrite', component: MessageWriteComponent,
        resolve: {
          globalSettings: GlobalSettingsResolve,
          whoAmI: WhoAmIResolve,
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'sessions', component: SessionsComponent,
        resolve: {
          whoAmI: WhoAmIResolve,
          accountData: AccountDataResolve
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'confirm-session', component: ConfirmSessionComponent,
        resolve: {
          globalSettings: GlobalSettingsResolve,
          whoAmI: WhoAmIResolve,
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'session-confirmed', component: SessionBookingConfirmedComponent,
        resolve: {
          globalSettings: GlobalSettingsResolve,
          whoAmI: WhoAmIResolve,
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'session-taken', component: SessionTakenComponent,
        resolve: {
          globalSettings: GlobalSettingsResolve,
          whoAmI: WhoAmIResolve,
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'session-payment', component: SessionPaymentComponent,
        resolve: {
          globalSettings: GlobalSettingsResolve,
          whoAmI: WhoAmIResolve,
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'settings', component: SettingsComponent,
        resolve: {
          whoAmI: WhoAmIResolve,
          globalSettings: GlobalSettingsResolve,
          accountData: AccountDataResolve
        },
        canActivate: [AuthGuard]
      }
    ]
  },

  //Public routing
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      { path: 'login', component: LoginComponent },
      { path: 'unlock', component: UnlockAccountComponent },
      { path: 'forgotten', component: ForgottenComponent },
      {
        path: 'resetpwd', component: ResetPasswordComponent,
        resolve: {
          globalSettings: GlobalSettingsResolve
        },
      },
      {
        path: 'search', component: SearchComponent,
        data: {
          enabledOnly: true
        },
        resolve: {
          globalSettings: GlobalSettingsResolve,
          countryList: CountryResolve,
          stateList: StateResolve,
          languageList: LanguageResolve,
          counsellingTopicList: CounsellingTopicResolve
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'results', component: SearchResultsComponent,
        resolve: {
          globalSettings: GlobalSettingsResolve
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'profile/:mbno', component: ProProfileComponent,
        resolve: {
          globalSettings: GlobalSettingsResolve,
          profile: CounsellorProfileResolve,
          whoAmI: WhoAmIResolve
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'redeem-session/:creditid', component: RedeemSessionComponent,
        resolve: {
          globalSettings: GlobalSettingsResolve,
          countryList: CountryResolve,
          stateList: StateResolve,
          languageList: LanguageResolve,
          counsellingTopicList: CounsellingTopicResolve,
          credit: SessionCreditResolve,
          whoAmI: WhoAmIResolve
        },
        canActivate: [AuthGuard]
      }
    ]
  },

  //Signup routing
  {
    path: '',
    component: SignupLayoutComponent,
    children: [
      {
        path: 'signup', component: Step1Component,
        resolve: { globalSettings: GlobalSettingsResolve },
      },
      {
        path: 'signup/NHS', component: Step1Component,
        resolve: { globalSettings: GlobalSettingsResolve },
      },
      {
        path: 'signup/Telus', component: Step1Component,
        resolve: { globalSettings: GlobalSettingsResolve },
      },
      {
        path: 'signup/:orgid', component: Step1Component,
        resolve: { globalSettings: GlobalSettingsResolve, organisation: OrgIDResolve },
      },
      {
        path: 'signup-2', component: Step2Component,
        resolve: { counsellingTopicList: CounsellingTopicResolve, reasonsForMedicationList: ReasonForMedicationResolve, globalSettings: GlobalSettingsResolve },
        canActivate: [SignupGuard]
      },
      {
        path: 'signup-2-not-suitable', component: Step2FailComponent,
        canActivate: [SignupGuard]
      },
      {
        path: 'signup-3', component: Step3Component,
        resolve: {
          countryList: CountryResolve, callingCodeList: CallingCodeResolve, stateList: StateResolve, nationalityList: NationalityResolve, globalSettings: GlobalSettingsResolve },
        canActivate: [SignupGuard]
      },
      {
        path: 'signup-4', component: Step4Component,
        canActivate: [SignupGuard]
      },
      {
        path: 'verification',
        component: Step5Component
      },
      {
        path: 'verification/:token',
        component: Step5Component
      },
    ]
  },

  //Error routing
  {
    path: '',
    component: ErrorLayoutComponent,
    children: [
      { path: '**', component: PageNotFoundComponent }
    ]
  }
];

export const routing = RouterModule.forRoot(appRoutes);
